import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
} from 'chart.js';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);

const ChartRenderer = ({ labelType, chartData, sampleSizes, labelTypes, asMixedSentiment, asPercentages, onBarClick }) => {
  const labelCounts = chartData[labelType];
  const isSentimentEnabled = labelTypes[labelType]?.sentimentEnabled;

  let data = {};
  let options = {};

  if (labelType === 'Sentiment') {
    const keys = ['positive', 'negative', 'mixed', 'neutral', 'negative/mixed'];
    const baseColors = { positive: '#2B1056', negative: '#A9393A', mixed: '#CB6C75', neutral: '#A7A4B0', "negative/mixed": '#B94B66'};  
    const sentimentCounts = {};
    const sentimentColors = {};

    keys.forEach((key) => {
      if (Array.isArray(labelCounts[key])) {
        sentimentCounts[key] = labelCounts[key][0] || 0;
      }
    });
    
    for (const key in baseColors) {
      if (labelCounts[key]) {
        sentimentColors[key] = baseColors[key];
      }
    }

    const sortedSentimentsData = Object.keys(sentimentCounts)
      .map((sentiment) => ({ sentiment, count: sentimentCounts[sentiment] }))
      .sort((a, b) => b.count - a.count); // Sort in descending order based on counts

    const sortedLabels = sortedSentimentsData.map((item) => item.sentiment);
    const sortedCounts = sortedSentimentsData.map((item) => item.count);
    const sortedColors = sortedLabels.map((label) => sentimentColors[label]);

    data = {
      labels: sortedLabels.map((label) => label.charAt(0).toUpperCase() + label.slice(1)),
      datasets: [
        {
          label: labelType, // Use dynamic labelType
          data: sortedCounts,
          backgroundColor: sortedColors,
        },
      ],
    };

    options = {
      indexAxis: 'y',
      onClick: (event, elements) => {
        if (elements.length > 0) {
          const clickedElement = elements[0];
          const sentimentLabel = sortedLabels[clickedElement.index];
          onBarClick('Sentiment', sentimentLabel, sentimentLabel.toLowerCase());
        }
      },
      scales: {
        x: { beginAtZero: true },
      },
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          display: false, // Disable legend
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const value = context.raw;

              if (asPercentages) 
                return `${context.dataset.label}: ${Array.isArray(value) ? `${(value[0] || 0).toFixed(2)}%` : `${(value || 0).toFixed(2)}%`}`;
              else 
                return `${context.dataset.label}: ${(value || 0)}`;
            },
          },
        },
      },
    };
  } else {
    const labels = Object.keys(labelCounts);

    const sortedLabels = labels.sort((a, b) => {
      const totalA = isSentimentEnabled ? labelCounts[a]?.reduce((acc, val) => acc + val, 0) : labelCounts[a];
      const totalB = isSentimentEnabled ? labelCounts[b]?.reduce((acc, val) => acc + val, 0) : labelCounts[b];
      return totalB - totalA;
    });

    const baseConfig = asMixedSentiment
      ? [
          { label: 'Positive', index: 0, color: '#2B1056' },
          { label: 'Negative/Mixed', index: 1, color: '#B94B66' },
          { label: 'Neutral', index: 2, color: '#A7A4B0' },
        ]
      : [
          { label: 'Positive', index: 0, color: '#2B1056' },
          { label: 'Negative', index: 1, color: '#A9393A' },
          { label: 'Mixed', index: 2, color: '#CB6C75' },
          { label: 'Neutral', index: 3, color: '#A7A4B0' },
        ];

    data = isSentimentEnabled
      ? {
        labels: sortedLabels.length > 0 ? sortedLabels : ['No Data'],
        datasets: baseConfig.map(({ label, index, color }) => ({
          label,
          data: sortedLabels.map((labelKey) => labelCounts[labelKey]?.[index] || 0),
          backgroundColor: color,
        })),
      }
      : {
        labels: sortedLabels.length > 0 ? sortedLabels : ['No Data'],
        datasets: [
          {
            label: labelType, // Use dynamic labelType
            data: sortedLabels.map((label) => labelCounts[label] || 0),
            backgroundColor: ['#3BC1BE'],
          },
        ],
      };

    options = {
      indexAxis: 'y',
      scales: {
        x: { beginAtZero: true, stacked: isSentimentEnabled },
        y: { stacked: isSentimentEnabled },
      },
      responsive: true,
      maintainAspectRatio: true,
      onClick: (evt, elements) => {
        if (elements.length > 0) {
          const datasetIndex = elements[0].datasetIndex;
          const labelIndex = elements[0].index;
          const clickedLabel = data.labels[labelIndex];
          const sentiment = isSentimentEnabled ? data.datasets[datasetIndex].label : null;
          onBarClick(labelType, clickedLabel, sentiment);
        }
      },
      plugins: {
        legend: {
          display: false, // Disable legend
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const value = context.raw;

              if (asPercentages) 
                return `${context.dataset.label}: ${Array.isArray(value) ? `${(value[0] || 0).toFixed(2)}%` : `${(value || 0).toFixed(2)}%`}`;
              else 
                return `${context.dataset.label}: ${(value || 0)}`;
            },
          },
        },
      },
    };
  }

  return <Bar data={data} options={options} width={500} height={250} />;
};

export default ChartRenderer;
